import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/lib/auth";
import { UserPlus, ShieldCheck, User as UserIcon, KeyRound, Trash2, Ban, CheckCircle2, X, Mail, History, Send, Clock, LogOut, Sparkles, Download, Upload } from "lucide-react";

/**
 * System Users — admin-only. Create/edit/deactivate/delete users who can
 * log into the admin console. Reuses the existing bcrypt + JWT auth stack.
 */
export default function Users() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null); // { id, name, role }
  const [resetting, setResetting] = useState(null); // user id
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "member", password_mode: "invite", sandbox_mode: false });
  const [pw, setPw] = useState("");
  const [audit, setAudit] = useState([]);
  const [auditActors, setAuditActors] = useState([]);
  const [auditActorFilter, setAuditActorFilter] = useState("");
  const [showAudit, setShowAudit] = useState(false);
  const [invites, setInvites] = useState([]);
  const [seeding, setSeeding] = useState(false);
  const [backing, setBacking] = useState(false);
  const [scheduledBackups, setScheduledBackups] = useState([]);
  const [verifying, setVerifying] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const isAdmin = (me?.role || "") === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setRows(data.users || []);
      try {
        const [a, i] = await Promise.all([
          api.get(`/users/audit-log?limit=100${auditActorFilter ? `&actor_email=${encodeURIComponent(auditActorFilter)}` : ""}`),
          api.get("/users/pending-invites"),
        ]);
        setAudit(a.data.log || []);
        setAuditActors(a.data.actors || []);
        setInvites(i.data.invites || []);
      } catch (auxErr) {
        console.debug("Users side-panel load failed:", auxErr?.message || auxErr);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [auditActorFilter]);

  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin, load]);

  const loadScheduledBackups = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/admin/backup/scheduled?limit=8");
      setScheduledBackups(data.backups || []);
    } catch { /* silent */ }
  }, [isAdmin]);

  useEffect(() => { loadScheduledBackups(); }, [loadScheduledBackups]);

  const killAllSessions = async () => {
    if (!window.confirm("Log EVERYONE out — including yourself and all other admins? Everyone must sign in again. Continue?")) return;
    try {
      const { data } = await api.post("/admin/sessions/kill-all");
      toast.success(`Killed sessions for ${data.users_invalidated} user(s) — re-sign in on other devices.`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const seedDemoLeads = async () => {
    if (!window.confirm("Seed 20 realistic demo leads (plumber / HVAC / roofer / electrician / landscaper) into the system? Any existing demo leads will be replaced. Sandbox-mode users see ONLY these.")) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/leads/seed-demo", { count: 20 });
      toast.success(`Seeded ${data.seeded} demo leads. Sandbox contractors now see them; you still see everything.`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSeeding(false); }
  };

  const stats = useMemo(() => ({
    total: rows.length,
    admins: rows.filter((r) => r.role === "admin").length,
    members: rows.filter((r) => r.role !== "admin").length,
    inactive: rows.filter((r) => r.active === false).length,
  }), [rows]);

  if (!isAdmin) {
    return (
      <div className="p-10" data-testid="users-page">
        <div className="bg-amber-50 border border-amber-200 p-8 max-w-lg">
          <h1 className="font-display text-2xl font-bold mb-2">Admins only</h1>
          <p className="text-neutral-700">You need the <code>admin</code> role to manage system users. Ask an existing admin to promote you.</p>
        </div>
      </div>
    );
  }

  const submitAdd = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = form.password_mode === "invite"
        ? { email: form.email, name: form.name, role: form.role, password_mode: "invite" }
        : { ...form };
      const { data } = await api.post("/users", body);
      if (form.password_mode === "invite") {
        // Auto-send the invite email
        try {
          await api.post(`/users/${data.id}/invite`);
          toast.success(`Added ${form.email} and emailed them a set-password link.`);
        } catch (err) {
          toast.error(`User added but invite email failed: ${formatApiError(err)}`);
        }
      } else {
        toast.success(`Added ${form.email}`);
      }
      setShowAdd(false);
      setForm({ email: "", password: "", name: "", role: "member", password_mode: "invite" });
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const sendInvite = async (u) => {
    if (!window.confirm(`Email a fresh set-password link to ${u.email}?`)) return;
    try {
      await api.post(`/users/${u.id}/invite`);
      toast.success(`Invite email sent to ${u.email}`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await api.patch(`/users/${editing.id}`, { name: editing.name, role: editing.role });
      toast.success("Updated");
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitPasswordReset = async () => {
    if (!resetting || pw.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await api.patch(`/users/${resetting}`, { password: pw });
      toast.success("Password updated");
      setResetting(null);
      setPw("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (u) => {
    if (u.id === me.id) return toast.error("You cannot deactivate yourself");
    try {
      await api.patch(`/users/${u.id}`, { active: !(u.active !== false) });
      toast.success(u.active === false ? "Reactivated" : "Deactivated");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (u) => {
    if (u.id === me.id) return toast.error("You cannot delete yourself");
    if (!window.confirm(`Delete ${u.email}? They will lose console access immediately.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadBackup = async () => {
    setBacking(true);
    try {
      const { data: prev } = await api.get("/admin/backup/preview");
      const totalDocs = prev.total_documents.toLocaleString();
      const encInfo = prev.encryption || {};
      const proceed = window.confirm(
        `Export ${totalDocs} records across ${prev.collection_count} collections?\n\n` +
        `You'll be asked next if you want to encrypt with a passphrase (AES-256-GCM). ` +
        `Encrypted backups are safe to store on any drive; plaintext must live in encrypted storage only.`
      );
      if (!proceed) { setBacking(false); return; }
      const pass = window.prompt(
        "Encryption passphrase (recommended — ≥ 8 characters):\n\n" +
        "• Leave blank for PLAINTEXT JSON (fast, no crypto — store in a vault).\n" +
        "• Enter a passphrase for AES-256-GCM encrypted download (.json.enc).\n\n" +
        "Save this passphrase somewhere safe — losing it means losing the backup.",
        ""
      );
      if (pass === null) { setBacking(false); return; }
      const passphrase = (pass || "").trim();
      if (passphrase && passphrase.length < 8) {
        toast.error("Passphrase must be at least 8 characters. Cancelled.");
        setBacking(false);
        return;
      }
      const res = await api.post("/admin/backup/export",
        passphrase ? { passphrase } : {},
        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const cd = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/.exec(cd);
      const filename = match ? match[1] : `clientsonramp-backup-${Date.now()}.json`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}${passphrase ? " (encrypted)" : ""}`);
      loadScheduledBackups();  // refresh in case a new manual entry shows up
    } catch (err) {
      toast.error(formatApiError(err) || "Backup failed");
    } finally {
      setBacking(false);
    }
  };

  const downloadScheduledBackup = async (b) => {
    try {
      const res = await api.get(`/admin/backup/download/${b.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = b.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${b.filename}`);
    } catch (err) {
      toast.error(formatApiError(err) || "Download failed");
    }
  };

  const onRestoreFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setRestoring(true);
    try {
      const looksEncrypted = /\.enc$/i.test(file.name);
      let passphrase = "";
      if (looksEncrypted) {
        const pp = window.prompt(`"${file.name}" looks encrypted (.enc). Enter the passphrase used to seal it:`, "");
        if (pp === null) { setRestoring(false); return; }
        passphrase = pp.trim();
        if (passphrase.length < 8) {
          toast.error("Passphrase must be at least 8 characters. Cancelled.");
          setRestoring(false); return;
        }
      }
      const fd = new FormData();
      fd.append("file", file);
      fd.append("passphrase", passphrase);
      fd.append("mode", "preview");
      const { data: preview } = await api.post("/admin/backup/restore", fd);
      const collCount = (preview.collections_to_replace || []).length;
      const fileDocs = (preview.file_total_documents || 0).toLocaleString();
      const currDocs = (preview.current_total_documents || 0).toLocaleString();
      const proceed = window.confirm(
        `Preview complete for ${file.name}\n\n` +
        `File contains ${fileDocs} docs across ${collCount} collections.\n` +
        `Current database has ${currDocs} docs across the same collections.\n\n` +
        `⚠ Continuing will DELETE all current data in those collections and replace it with the file's contents. ` +
        `Users, leads, contracts — everything in the listed collections goes away.\n\n` +
        `This cannot be undone. Continue?`
      );
      if (!proceed) {
        toast.success(`Preview only — nothing changed. File has ${fileDocs} docs across ${collCount} collections.`);
        setRestoring(false); return;
      }
      const fd2 = new FormData();
      fd2.append("file", file);
      fd2.append("passphrase", passphrase);
      fd2.append("mode", "replace");
      fd2.append("confirm_replace", "1");
      const { data: result } = await api.post("/admin/backup/restore", fd2);
      const total = Object.values(result.report || {}).reduce((s, r) => s + (r.inserted || 0), 0);
      toast.success(`Restore complete · ${total.toLocaleString()} documents inserted. Refresh to see the new data.`);
    } catch (err) {
      toast.error(formatApiError(err) || "Restore failed");
    } finally {
      setRestoring(false);
    }
  };

  const verifyBackup = async (b) => {
    setVerifying(b.id);
    try {
      let url = `/admin/backup/verify/${b.id}`;
      if (b.encrypted) {
        const pp = window.prompt(
          `Verify "${b.filename}"\n\nEnter the passphrase used to encrypt this backup. ` +
          `We'll decrypt server-side, parse the JSON, and confirm every collection total still matches — nothing is shown or exported.`,
          ""
        );
        if (pp === null) { setVerifying(null); return; }
        url += `?passphrase=${encodeURIComponent(pp)}`;
      }
      const { data } = await api.get(url);
      if (data.ok) {
        toast.success(
          `✓ Valid · ${(data.total_documents || 0).toLocaleString()} docs across ${data.collection_count} collections · ${(data.size / 1024).toFixed(0)} KB`
        );
      } else if (data.error) {
        toast.error(`Verify failed: ${data.error}`);
      } else if ((data.mismatches || []).length) {
        const summary = data.mismatches.slice(0, 3).map((m) => `${m.collection} recorded=${m.recorded} actual=${m.actual}`).join(" · ");
        toast.error(`Doc-count mismatch: ${summary}`);
      } else if (data.parse_error) {
        toast.error(`Parse error: ${data.parse_error}`);
      } else {
        toast.error("Verification failed — see console for details.");
        console.warn("verify result:", data);
      }
    } catch (err) {
      toast.error(formatApiError(err) || "Verify failed");
    } finally {
      setVerifying(null);
    }
  };

  return (
    <div className="p-10" data-testid="users-page">
      <Toaster position="top-right" richColors />

      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="overline mb-2">Back office</div>
          <h1 className="font-display text-4xl font-black tracking-tight">System users</h1>
          <p className="text-neutral-600 mt-2 max-w-2xl">
            Manage everyone who can sign in to the Clients OnRamp admin console. Admins can do everything; members inherit read-mostly access.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={downloadBackup} disabled={backing} data-testid="users-backup-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-emerald-500 text-emerald-800 hover:bg-emerald-50 text-sm font-semibold transition disabled:opacity-50"
                  title="Download a full offline JSON snapshot of every business collection (leads, clients, contracts, audits, users…)">
            <Download className="w-4 h-4" /> {backing ? "Preparing…" : "Download backup"}
          </button>
          <button onClick={() => document.getElementById("restore-file-input").click()} data-testid="users-restore-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-rose-500 text-rose-700 hover:bg-rose-50 text-sm font-semibold transition"
                  title="Upload a previously-downloaded backup file to restore. Preview first — destructive replace requires explicit confirmation.">
            <Upload className="w-4 h-4" /> Restore
          </button>
          <input id="restore-file-input" type="file" accept=".json,.enc,application/json,application/octet-stream" style={{ display: "none" }} onChange={onRestoreFilePicked} data-testid="users-restore-file-input" />
          <button onClick={seedDemoLeads} disabled={seeding} data-testid="users-seed-demo-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-amber-400 text-amber-800 hover:bg-amber-50 text-sm font-semibold transition disabled:opacity-50"
                  title="Seed 20 realistic demo leads for sandbox-mode users to practice on">
            <Sparkles className="w-4 h-4" /> {seeding ? "Seeding…" : "Seed 20 demo leads"}
          </button>
          <button
            onClick={killAllSessions}
            data-testid="users-kill-all-sessions-btn"
            className="inline-flex items-center gap-2 px-4 py-2 border border-rose-400 text-rose-700 hover:bg-rose-50 text-sm font-semibold transition"
            title="Invalidate every active JWT session — everyone must sign in again"
          >
            <LogOut className="w-4 h-4" /> Kill all sessions
          </button>
          <button onClick={() => setShowAdd(true)} data-testid="users-add-btn" className="lf-btn-primary inline-flex items-center gap-2">
            <UserPlus className="w-4 h-4" /> Add user
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard testId="users-stat-total" label="Total users" value={stats.total} icon={<UserIcon className="w-4 h-4" />} />
        <StatCard testId="users-stat-admins" label="Admins" value={stats.admins} icon={<ShieldCheck className="w-4 h-4" />} />
        <StatCard testId="users-stat-members" label="Members" value={stats.members} icon={<UserIcon className="w-4 h-4" />} />
        <StatCard testId="users-stat-inactive" label="Deactivated" value={stats.inactive} icon={<Ban className="w-4 h-4" />} />
      </div>

      {/* Scheduled auto-backups */}
      {isAdmin && (
        <div className="mb-8 bg-white border border-emerald-200 border-l-4 border-l-emerald-500 p-5" data-testid="users-scheduled-backups">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h2 className="font-display text-lg font-bold flex items-center gap-2">
                <Download className="w-4 h-4 text-emerald-600" /> Scheduled backups
                <span className="text-[11px] font-mono uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5">weekly · Sun 02:00 UTC</span>
              </h2>
              <p className="text-xs text-neutral-600 mt-1">
                Last 8 auto-snapshots. Set <code className="text-[11px] bg-neutral-100 px-1">BACKUP_PASSPHRASE</code> in your env for AES-256 encryption. Delivered to your inbox weekly with a signed download link.
              </p>
            </div>
          </div>
          {scheduledBackups.length === 0 ? (
            <div className="text-sm text-neutral-500 py-3 italic" data-testid="scheduled-backups-empty">
              No auto-backups yet — the first one will land Sunday 02:00 UTC. Run <code className="text-[11px] bg-neutral-100 px-1">python -m backup_scheduled_cron</code> from the backend to trigger one on demand.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="scheduled-backups-list">
              {scheduledBackups.map((b) => (
                <div
                  key={b.id}
                  data-testid={`scheduled-backup-${b.id}`}
                  className="border border-neutral-200 hover:border-emerald-500 hover:bg-emerald-50/50 p-3 transition"
                  title={b.encrypted ? "AES-256-GCM encrypted — needs your passphrase to open" : "Plaintext JSON"}
                >
                  <button
                    onClick={() => downloadScheduledBackup(b)}
                    className="w-full text-left"
                    data-testid={`scheduled-backup-download-${b.id}`}
                  >
                    <div className="font-mono text-[11px] text-neutral-500 mb-1 truncate">{b.filename}</div>
                    <div className="text-sm font-semibold text-neutral-800">
                      {b.total_documents?.toLocaleString?.() || 0} docs · {(b.size / 1024).toFixed(0)} KB
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-2 flex items-center gap-2">
                      <span>{new Date(b.created_at).toLocaleDateString()}</span>
                      <span className={b.encrypted ? "text-emerald-700 font-mono" : "text-amber-700 font-mono"}>
                        {b.encrypted ? "🔒 encrypted" : "⚠ plaintext"}
                      </span>
                    </div>
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); verifyBackup(b); }}
                    disabled={verifying === b.id}
                    data-testid={`scheduled-backup-verify-${b.id}`}
                    className="w-full mt-3 pt-2 border-t border-neutral-100 text-[11px] font-mono uppercase tracking-widest text-emerald-700 hover:text-emerald-900 disabled:opacity-50 inline-flex items-center justify-center gap-1"
                    title="Decrypt + parse + doc-count-check the file server-side. No download."
                  >
                    <CheckCircle2 className="w-3 h-3" /> {verifying === b.id ? "Verifying…" : "Verify integrity"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pending invites */}
      {invites.length > 0 && (
        <div className="mb-8 bg-white border border-blue-200 border-l-4 p-5" data-testid="users-pending-invites">
          <h2 className="font-display text-lg font-bold flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-blue-600" /> Pending invites ({invites.length})
          </h2>
          <p className="text-xs text-neutral-600 mb-3">
            Users who haven&apos;t clicked their set-password link yet. Resend if their invite is about to expire.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {invites.map((inv) => {
              const hoursLeft = Math.round((new Date(inv.expires_at) - new Date()) / 3600000);
              return (
                <div key={inv.token || inv.user_id + inv.created_at} className="border border-neutral-200 p-3 text-sm">
                  <div className="font-mono text-xs">{inv.email}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    {inv.target_name || "—"} · <span className="uppercase tracking-widest text-[10px]">{inv.target_role || "member"}</span>
                  </div>
                  <div className="text-[11px] text-neutral-500 mt-2">
                    Expires in <strong>{hoursLeft}h</strong> · sent by {inv.sent_by}
                  </div>
                  <button
                    onClick={() => sendInvite({ id: inv.user_id, email: inv.email })}
                    data-testid={`invite-resend-${inv.user_id}`}
                    className="mt-2 text-xs bg-blue-600 text-white px-3 py-1 hover:bg-blue-800 inline-flex items-center gap-1">
                    <Send className="w-3 h-3" /> Resend
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {loading ? <div>Loading…</div> : (
        <div className="bg-white border border-neutral-200 overflow-x-auto">
          <table className="lf-table w-full text-sm" data-testid="users-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => {
                const isSelf = u.id === me?.id;
                const isEditing = editing?.id === u.id;
                return (
                  <tr key={u.id} data-testid={`users-row-${u.id}`}>
                    <td className="font-mono text-xs">
                      {u.email}
                      {isSelf && <span className="ml-2 bg-blue-50 text-blue-700 px-1.5 py-0.5 text-[10px]">YOU</span>}
                    </td>
                    <td>
                      {isEditing ? (
                        <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                               data-testid={`users-edit-name-${u.id}`} className="lf-input" />
                      ) : (u.name || <span className="text-neutral-400">—</span>)}
                    </td>
                    <td>
                      {isEditing ? (
                        <select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })}
                                data-testid={`users-edit-role-${u.id}`} className="lf-input" disabled={isSelf}>
                          <option value="admin">Admin</option>
                          <option value="member">Member</option>
                          <option value="marketing">Marketing</option>
                        </select>
                      ) : (
                        <span className={`text-xs font-semibold px-2 py-0.5 uppercase tracking-widest ${
                          u.role === "admin" ? "bg-[#0F2340] text-white"
                          : u.role === "marketing" ? "bg-[#E24E1B] text-white"
                          : "bg-neutral-100 text-neutral-700"
                        }`}>{u.role || "member"}</span>
                      )}
                    </td>
                    <td>
                      {u.active === false ? (
                        <span className="text-xs bg-amber-50 border border-amber-200 text-amber-800 px-2 py-0.5">Deactivated</span>
                      ) : (
                        <span className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 px-2 py-0.5">Active</span>
                      )}
                    </td>
                    <td className="text-xs text-neutral-500 font-mono">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      {isEditing ? (
                        <>
                          <button onClick={saveEdit} disabled={busy} data-testid={`users-save-${u.id}`}
                                  className="text-emerald-700 hover:text-emerald-900 p-1 mr-1"><CheckCircle2 className="w-4 h-4" /></button>
                          <button onClick={() => setEditing(null)} className="text-neutral-500 hover:text-neutral-800 p-1"><X className="w-4 h-4" /></button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => setEditing({ id: u.id, name: u.name || "", role: u.role || "member" })}
                                  data-testid={`users-edit-${u.id}`}
                                  className="lf-btn-outline text-xs px-2 py-1 mr-1">Edit</button>
                          <button onClick={() => { setResetting(u.id); setPw(""); }}
                                  data-testid={`users-reset-pw-${u.id}`}
                                  title="Reset password"
                                  className="text-neutral-600 hover:text-[#0F2340] p-1 mr-1"><KeyRound className="w-3.5 h-3.5" /></button>
                          <button onClick={() => sendInvite(u)}
                                  data-testid={`users-invite-${u.id}`}
                                  title="Email set-password invite"
                                  className="text-blue-600 hover:text-blue-800 p-1 mr-1"><Mail className="w-3.5 h-3.5" /></button>
                          <button onClick={() => toggleActive(u)}
                                  data-testid={`users-toggle-${u.id}`}
                                  disabled={isSelf}
                                  title={u.active === false ? "Reactivate" : "Deactivate"}
                                  className="text-amber-700 hover:text-amber-900 p-1 mr-1 disabled:opacity-30"><Ban className="w-3.5 h-3.5" /></button>
                          <button onClick={() => del(u)}
                                  data-testid={`users-delete-${u.id}`}
                                  disabled={isSelf}
                                  className="text-red-600 hover:text-red-800 p-1 disabled:opacity-30"><Trash2 className="w-3.5 h-3.5" /></button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Audit log */}
      {audit.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={() => setShowAudit((s) => !s)}
                    data-testid="users-audit-toggle"
                    className="font-display text-lg font-bold flex items-center gap-2 text-neutral-700 hover:text-black">
              <History className="w-4 h-4" /> Audit log ({audit.length}) {showAudit ? "▾" : "▸"}
            </button>
            {showAudit && auditActors.length > 0 && (
              <select
                data-testid="users-audit-actor-filter"
                value={auditActorFilter}
                onChange={(e) => setAuditActorFilter(e.target.value)}
                className="text-xs border border-neutral-300 px-2 py-1 bg-white ml-auto"
                title="Show only actions by this user"
              >
                <option value="">All actors ({auditActors.length})</option>
                {auditActors.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            )}
          </div>
          {showAudit && (
            <div className="mt-3 bg-white border border-neutral-200 max-h-96 overflow-y-auto" data-testid="users-audit-log">
              <table className="lf-table w-full text-xs">
                <thead><tr><th>When</th><th>Actor</th><th>Action</th><th>Target</th></tr></thead>
                <tbody>
                  {audit.length === 0 && (
                    <tr><td colSpan={4} className="text-center text-neutral-400 py-6 text-xs">No entries match the current filter.</td></tr>
                  )}
                  {audit.map((e) => (
                    <tr key={e.id}>
                      <td className="text-neutral-500 font-mono">{new Date(e.created_at).toLocaleString()}</td>
                      <td className="font-mono">{e.actor_email || "—"}</td>
                      <td><span className="uppercase tracking-widest text-[10px] font-semibold bg-neutral-100 px-2 py-0.5">{e.action}</span></td>
                      <td className="font-mono">{e.target_email || (e.target_id ? e.target_id.slice(0, 8) + "…" : "—")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add user modal */}
      {showAdd && (
        <Modal title="Add system user" onClose={() => setShowAdd(false)}>
          <form onSubmit={submitAdd} className="space-y-4">
            <Field label="Email">
              <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                     data-testid="users-form-email" className="lf-input" />
            </Field>
            <Field label="Full name">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="users-form-name" className="lf-input" />
            </Field>
            <Field label="Password mode">
              <select value={form.password_mode}
                      onChange={(e) => setForm({ ...form, password_mode: e.target.value })}
                      data-testid="users-form-pw-mode" className="lf-input">
                <option value="invite">Email invite (recommended — user sets their own password)</option>
                <option value="manual">Manual password (you set + share it)</option>
              </select>
            </Field>
            {form.password_mode === "manual" && (
              <Field label="Password (min 8 characters)">
                <input type="password" required minLength={8} value={form.password}
                       onChange={(e) => setForm({ ...form, password: e.target.value })}
                       data-testid="users-form-password" className="lf-input" />
              </Field>
            )}
            <Field label="Role">
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                      data-testid="users-form-role" className="lf-input">
                <option value="member">Member — full console access (no user mgmt)</option>
                <option value="marketing">Marketing — leads + outreach only (safe for contractors)</option>
                <option value="admin">Admin — full access including billing + settings</option>
              </select>
            </Field>
            {form.role === "marketing" && (
              <Field label="Sandbox mode (contractor trial)">
                <label className="flex items-center gap-2 text-sm text-neutral-700 py-2">
                  <input type="checkbox" data-testid="users-form-sandbox"
                         checked={form.sandbox_mode || false}
                         onChange={(e) => setForm({ ...form, sandbox_mode: e.target.checked })}
                         className="w-4 h-4"/>
                  Restrict to demo leads only (they can't see or touch real prospects)
                </label>
                <p className="text-xs text-neutral-500 mt-1">Toggle off once you trust them. You can seed 20 demo leads from the Sandbox card below.</p>
              </Field>
            )}
            <div className="flex gap-2 justify-end pt-2">
              <button type="button" onClick={() => setShowAdd(false)} className="lf-btn-outline">Cancel</button>
              <button type="submit" disabled={busy} data-testid="users-form-submit" className="lf-btn-primary">
                {busy ? "Adding…" : "Add user"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Reset password modal */}
      {resetting && (
        <Modal title="Reset password" onClose={() => { setResetting(null); setPw(""); }}>
          <div className="space-y-4">
            <p className="text-sm text-neutral-600">
              Set a new password for this user. Share it with them securely — they should change it on first login.
            </p>
            <input type="password" placeholder="New password (min 8 chars)" minLength={8}
                   value={pw} onChange={(e) => setPw(e.target.value)}
                   data-testid="users-reset-input" className="lf-input" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setResetting(null); setPw(""); }} className="lf-btn-outline">Cancel</button>
              <button onClick={submitPasswordReset} disabled={busy || pw.length < 8}
                      data-testid="users-reset-submit" className="lf-btn-primary">
                {busy ? "Saving…" : "Set password"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function StatCard({ label, value, icon, testId }) {
  return (
    <div className="bg-white border border-neutral-200 p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-widest mb-2">
        {icon} {label}
      </div>
      <div className="font-display text-3xl font-bold text-[#0F2340]">{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline block mb-2">{label}</span>
      {children}
    </label>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white border border-neutral-200 p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-display text-xl font-bold">{title}</h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
